import React, { useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { Member } from "Models/members/member";
import { Filters } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { handlesGrid } from "Util/handlesGrid";

import { ColumnStatus } from "./styles";
import { useLoginSessionsActions } from "../../../Tabs/Details/useActions";
import { useAppState } from "Context/StateContext";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

interface LoginSessionsInterfac {
  member: Member;
}

export const LoginSessions = (props: LoginSessionsInterfac) => {
  const { member } = props;
  const { t: translate } = useTranslation();

  const [filters, setFilters] = useState<Filters>({} as Filters);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });

  const { getSessions, getSessionsCount } = useLoginSessionsActions(
    member.userId,
    member.companyId
  );

  const { data: sessions, isFetching } = getSessions(page, pageSize, filters);

  const { data: sessionCount } = getSessionsCount(filters);

  const { currentUser } = useAppState();

  const handleFilter = (value: Filters) => {
    setPage(0);
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : ({} as Filters));
  };

  const columns: Column[] = [
    {
      name: "deviceInfo", // application
      title: translate("application"),
      formatComponent: (value, row) => {
        if (!value) return "-";
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : (value as any);

          if (parsed?.browser) return <span>{parsed.browser}</span>;
          const requestApp = parsed?.request?.split('/')?.[0];

          if (requestApp) {
            if (requestApp === "okhttp") {
              if (row?.platform && row?.platform === "ops") return <span>Shipeezi OPS</span>;
              else if (!row?.platform) return <span>Shipeezi Sales</span>;
            }

            return <span>{requestApp}</span>;
          }



          return <span>{"-"}</span>;
        } catch (error) {
          // Quick fix to prevent white screen
          console.error("Failed to parse deviceInfo JSON:", error, { value });
          return "-";
        }
      },
    },
    {
      name: "platform",
      title: translate("shipeezi_platform"),
      formatComponent: (value) => {
        if (!value) return "-";

        switch (value) {
          case "web":
            return "Shipeezi web";
          case "ops":
            return "Operations";
          case "sales":
            return "Sales App";
          default:
            return value;
        }
      },
    },
    {
      name: "lastActivity",
      title: translate("last_active_date"),
      formatComponent: (value) => {
        if (!value) return "-";

        let text = "";
        const now = new Date();
        const date = new Date(value);

        const diffInMs = now.getTime() - date.getTime();
        const diffInSeconds = Math.floor(diffInMs / 1000);
        const minutes = Math.floor(diffInSeconds / 60);

        if (diffInSeconds < 60) text = "just now";
        else if (minutes < 60) text = `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
        else {
          text = formatDateWithUserPref(value, currentUser?.preferences ?? {}, "full");
        }

        return <span>{text}</span>;
      },
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value) => {
        const backgroundColor = value === "Active" ? colors.success50 : colors.gray100;
        const color = value === "Active" ? colors.success500 : colors.gray700;

        let text = value === "Active" ? value : "Logged out";
        return (
          <ColumnStatus>
            <BadgeStatus
              value={text}
              useExternalColor
              backgroundColor={backgroundColor}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleFilterBy"],
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("shipeezi_platform"),
          column: "platform",
          options: [
            { label: "Shipeezi web", value: "web" },
            { label: "Operations", value: "ops" },
            { label: "Sales App", value: "sales" },
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [
            { label: "Active", value: "Active" },
            { label: "Logged out", value: "Inactive" },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <ShipeeziNewGrid
      loadingData={isFetching}
      data={sessions || []}
      totalItems={sessionCount || 0}
      columns={columns}
      showSelection={false}
      showSort={false}
      currentPage={page}
      pageSize={pageSize}
      pageSizeList={[10, 20, 40, 60]}
      onChangePageSize={handlePageSize}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      showFooterGrid
      positionActionsColumn="right"
      generalActions={generalActions}
      keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
      emptyData={translate("noInformationToBeShown")}
      alwaysShowFooter={true}
    />
  );
};
